/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path utils
 * @description Color utilities
 */
export const stringToRGBA = (colorString: string) => {
  let color = colorString;
  if (colorString.indexOf("(") > 0) {
    if (colorString.indexOf("rgb") === 0) {
      const colorList = colorString
        .substring(colorString.indexOf("(") + 1, colorString.indexOf(")"))
        .split(",")
        .map((value: string) => parseFloat(value));
      return {
        r: colorList[0],
        g: colorList[1],
        b: colorList[2],
        a: colorList[3] || 1,
      };
    }
  }
  if (colorString[0] === "#") {
    color = colorString.substring(1);
  }
  // #abc or #abca
  if (color.length === 3 || color.length === 4) {
    const [r, g, b, a] = color.split("");
    return {
      r: parseInt(`${r}${r}`, 16),
      g: parseInt(`${g}${g}`, 16),
      b: parseInt(`${b}${b}`, 16),
      a: parseInt(`${a || "f"}${a || "f"}`, 16) / 255,
    };
  }
  // #aabbccaa
  if (color.length === 6 || color.length === 8) {
    const [r1, r2, g1, g2, b1, b2, a1, a2] = color.split("");
    return {
      r: parseInt(`${r1}${r2}`, 16),
      g: parseInt(`${g1}${g2}`, 16),
      b: parseInt(`${b1}${b2}`, 16),
      a: parseInt(`${a1 || "f"}${a2 || "f"}`, 16) / 255,
    };
  }
  return {
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  };
};
export type HSLA = {
  h: number;
  s: number;
  l: number;
  a: number;
};
export type RGBA = {
  r: number;
  g: number;
  b: number;
  a: number;
};
export const HSLAToString = (hsla: HSLA) => {
  return `hsla(${hsla.h.toFixed(0)},${hsla.s.toFixed(1)}%,${hsla.l.toFixed(
    1
  )}%, ${hsla.a})`;
};
export const RGBAToHSLA = (rgba: RGBA): HSLA => {
  let { r, g, b, a } = rgba;
  // Make r, g, and b fractions of 1
  r /= 255;
  g /= 255;
  b /= 255;
  // Find greatest and smallest channel values
  let cmin = Math.min(r, g, b);
  let cmax = Math.max(r, g, b);
  let delta = cmax - cmin;
  let h = 0;
  let s = 0;
  let l = 0;
  // Calculate hue
  // No difference
  if (delta === 0) h = 0;
  // Red is max
  else if (cmax === r) h = ((g - b) / delta) % 6;
  // Green is max
  else if (cmax === g) h = (b - r) / delta + 2;
  // Blue is max
  else h = (r - g) / delta + 4;
  h = Math.round(h * 60);
  // Make negative hues positive behind 360°
  if (h < 0) h += 360;
  // Calculate lightness
  l = (cmax + cmin) / 2;
  // Calculate saturation
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  // Multiply l and s by 100
  s = +(s * 100);
  l = +(l * 100);
  return {
    h,
    s,
    l,
    a,
  };
};
export const findMaxContrastColor = (colorString: string) => {
  const rgba = stringToRGBA(colorString);
  const hsla = RGBAToHSLA(rgba);
  if (hsla.l < 50) {
    hsla.l = 100;
  } else {
    hsla.l = 0;
  }
  return HSLAToString(hsla);
};
export const changeColorBrightness = (colorString: string, amount: number) => {
  const rgba = stringToRGBA(colorString);
  const hsla = RGBAToHSLA(rgba);
  hsla.l *= amount;
  return HSLAToString(hsla);
};
